/**
 * Schedule panel — shows stop-by-stop timetable when a vehicle is selected.
 *
 * Rendered below JourneyInfoPanel. Shows vertical timeline with progress,
 * delay badges, and click-to-pan-map interaction.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useMapCommandStore } from '../../stores/mapCommandStore';
import { getJourneySchedule, queryKeys } from '../../services/api';
import { findNextStopIndex, computeDelay, fmtHHMM } from '../../utils/scheduleUtils';
import type { ScheduleStop } from '../../types/api';

export interface SchedulePanelProps {
  className?: string;
  /** When true, renders without fixed positioning (for embedding in BottomSheet). */
  embedded?: boolean;
}

export function SchedulePanel({ className = '', embedded = false }: SchedulePanelProps) {
  const selectedVehicle = useVehicleStore(
    useShallow((state) => {
      if (!state.selectedVehicleId) return null;
      const v = state.vehicles.get(state.selectedVehicleId);
      if (!v) return null;
      return {
        journey_gid: v.journey_gid,
        line: v.line,
      };
    })
  );

  const journeyGid = selectedVehicle?.journey_gid ?? '';
  const flyTo = useMapCommandStore((s) => s.flyTo);

  // Expanded stop row (shows platform, full times).
  // Reset when vehicle changes by keying on journey_gid.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const prevGidRef = useRef(journeyGid);
  if (prevGidRef.current !== journeyGid) {
    prevGidRef.current = journeyGid;
    if (expandedIndex !== null) setExpandedIndex(null);
  }

  // Auto-scroll debounce: skip if user scrolled manually in last 5s
  const lastUserScrollRef = useRef(0);
  const nextStopRef = useRef<HTMLButtonElement | null>(null);

  // Fetch schedule data
  const { data: schedule, isLoading, isError } = useQuery({
    queryKey: queryKeys.journeys.schedule(journeyGid),
    queryFn: () => getJourneySchedule(journeyGid),
    enabled: !!journeyGid,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const stops = useMemo(() => schedule?.stops ?? [], [schedule?.stops]);
  const nextStopIdx = useMemo(() => findNextStopIndex(stops), [stops]);

  // Auto-scroll to next stop (skip in embedded/mobile mode)
  useEffect(() => {
    if (embedded) return;
    if (!nextStopRef.current) return;
    const now = Date.now();
    if (now - lastUserScrollRef.current < 5_000) return;
    nextStopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [nextStopIdx, embedded, isLoading]);

  const handleUserScroll = useCallback(() => {
    lastUserScrollRef.current = Date.now();
  }, []);

  const handleStopClick = useCallback(
    (stop: ScheduleStop, index: number) => {
      flyTo(stop.lat, stop.lon, 16);
      setExpandedIndex((prev) => (prev === index ? null : index));
    },
    [flyTo]
  );

  // Determine the accent color from the selected vehicle's line
  const accentColor = selectedVehicle?.line?.color_bg ?? '#3b82f6';

  const wrapperClass = `${className}`;

  if (!selectedVehicle) return null;

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={wrapperClass}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Schedule</p>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="flex-1 h-4 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="w-10 h-4 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={wrapperClass}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Schedule</p>
          <p className="text-sm text-red-600 dark:text-red-400">Failed to load schedule data.</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (stops.length === 0) {
    return (
      <div className={wrapperClass}>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Schedule</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Schedule not available for this journey.
          </p>
        </div>
      </div>
    );
  }

  const journeyComplete = nextStopIdx >= stops.length;

  return (
    <div className={wrapperClass}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        {/* Header */}
        <div className="px-3 pt-3 pb-1">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Schedule
            {journeyComplete && (
              <span className="ml-2 text-[10px] font-normal normal-case text-gray-400 dark:text-gray-500">
                Journey complete
              </span>
            )}
          </p>
        </div>

        {/* Stop list with timeline */}
        <div
          onScroll={handleUserScroll}
          className="overflow-y-auto max-h-[32rem] px-1 pb-2"
        >
          {stops.map((stop, i) => {
            const isPassed = i < nextStopIdx;
            const isNext = i === nextStopIdx;
            const isCancelled = stop.is_cancelled === true;
            const delay = !isCancelled ? computeDelay(stop) : null;
            const isExpanded = expandedIndex === i;
            const isFirst = i === 0;
            const isLast = i === stops.length - 1;

            // Show best available time: estimated/actual if present, otherwise planned.
            // Tilde prefix when showing planned-only time (no realtime data).
            const bestTime = stop.estimated_departure ?? stop.planned_departure ?? stop.planned_arrival;
            const isPlannedOnly = !stop.estimated_departure && !stop.is_actual;
            const timeStr = fmtHHMM(bestTime);
            const displayTime = isPlannedOnly ? `~${timeStr}` : timeStr;

            return (
              <button
                key={`${stop.gid}-${stop.index}`}
                ref={isNext ? nextStopRef : undefined}
                onClick={() => handleStopClick(stop, i)}
                aria-current={isNext ? 'step' : undefined}
                className={`
                  w-full text-left flex items-start gap-0 px-2 py-1.5
                  hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded transition-colors
                  focus:outline-none focus:ring-2 focus:ring-blue-500/50
                  ${isNext ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''}
                `}
              >
                {/* Timeline column — continuous vertical line with dot overlay */}
                <div className="relative flex flex-col items-center w-5 flex-shrink-0 mr-2 self-stretch">
                  {/* Continuous vertical line (full height of row) */}
                  <div
                    className={`absolute w-0.5 ${isFirst ? 'top-1/2' : 'top-0'} ${isLast ? 'bottom-1/2' : 'bottom-0'}`}
                    style={{
                      backgroundColor: isPassed
                        ? '#9ca3af'
                        : isNext
                          ? accentColor
                          : '#d1d5db',
                    }}
                  />

                  {/* Stop dot (centered, overlays the line) */}
                  <div
                    className={`
                      relative rounded-full flex-shrink-0 mt-2
                      ${isNext ? 'w-4 h-4 border-[3px] ring-2 ring-offset-1' : 'w-2.5 h-2.5 border-2'}
                      ${isCancelled ? 'border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-gray-700' : ''}
                    `}
                    style={
                      isCancelled
                        ? undefined
                        : isNext
                          ? { backgroundColor: accentColor, borderColor: accentColor, '--tw-ring-color': `${accentColor}40` } as React.CSSProperties
                          : isPassed
                            ? { backgroundColor: '#d1d5db', borderColor: '#9ca3af' }
                            : { backgroundColor: 'white', borderColor: accentColor }
                    }
                  />
                </div>

                {/* Stop content */}
                <div className="flex-1 min-w-0 py-0.5">
                  <div className="flex items-center justify-between gap-2">
                    {/* Stop name */}
                    <span
                      className={`
                        text-sm truncate
                        ${isCancelled ? 'line-through text-gray-400 dark:text-gray-500' : ''}
                        ${isPassed && !isCancelled ? 'text-gray-400 dark:text-gray-500' : ''}
                        ${isNext ? 'font-semibold text-gray-900 dark:text-gray-100' : ''}
                        ${!isPassed && !isNext && !isCancelled ? 'text-gray-700 dark:text-gray-300' : ''}
                      `}
                    >
                      {stop.name.replace(/, Göteborg$/, '')}
                    </span>

                    {/* Time + delay */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span
                        className={`text-xs font-mono ${
                          isPassed
                            ? 'text-gray-400 dark:text-gray-500'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {displayTime}
                      </span>
                      {delay && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${delay.colorClass}`}
                        >
                          {delay.text}
                        </span>
                      )}
                      {isCancelled && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          Cancelled
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                      {stop.planned_arrival && (
                        <div>
                          <span className="text-gray-400 dark:text-gray-500">Arr:</span>{' '}
                          {fmtHHMM(stop.planned_arrival)}
                          {stop.estimated_arrival && stop.estimated_arrival !== stop.planned_arrival && (
                            <span className="ml-1 text-gray-600 dark:text-gray-300">
                              → {fmtHHMM(stop.estimated_arrival)}
                            </span>
                          )}
                        </div>
                      )}
                      {stop.planned_departure && (
                        <div>
                          <span className="text-gray-400 dark:text-gray-500">Dep:</span>{' '}
                          {fmtHHMM(stop.planned_departure)}
                          {stop.estimated_departure && stop.estimated_departure !== stop.planned_departure && (
                            <span className="ml-1 text-gray-600 dark:text-gray-300">
                              → {fmtHHMM(stop.estimated_departure)}
                            </span>
                          )}
                        </div>
                      )}
                      {stop.platform && (
                        <div>
                          <span className="text-gray-400 dark:text-gray-500">Platform:</span>{' '}
                          {stop.platform}
                        </div>
                      )}
                      {stop.has_realtime && !stop.is_actual && (
                        <span className="inline-block text-[10px] px-1 py-0.5 rounded bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300">
                          LIVE
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
